import React, { useContext, useEffect, useState } from "react";
import { FormModal } from "../Shared/form";
import { FormContext, FormSchemaFields, ToggleSize } from "@stormwater-software/lib-form";
import { UIControlType } from "@stormwater-software/lib-form-control-types";
import inspectionIntervalsApi from "../../utils/api/inspectionIntervals";
import validator from "../../utils/FormValidator"
import AppContext from "../../contexts/AppContext";
import TenantFeatureContext from "../../contexts/TenantContext";
import ConfirmationModal from "../Shared/ConfirmationModal/ConfirmationModal";

// SW2-2152: cost_value is editable only by these roles (mirrors the server guard).
const COST_ROLES = ["Super Admin", "Admin", "Billing Admin"];

function getSchema(conditionalFormSchemaProperty:any, conditionalTwiceWeeklyProperties:any, formDataClone:any, canEditCost:boolean, featureOn:boolean) {
  return {
    name: {
      label: 'Name',
      autoComplete: 'organization1',
      validation: {
        required: true,
      },
    },
    interval: {
      label: 'Interval',
      parse: validator.parseDecimal,
      placeholder: '',
      autoComplete: 'address-line1',
      'aria-label': 'Street address line 1 of 2',
      validation: {
        // SW2-2334: the number carries no meaning for an On Demand interval, so
        // requiring it would block saving one.
        required: !formDataClone.on_demand,
      },
      style: {
        flexBasis: '33%',
      },
      disabled: formDataClone.on_demand || (!formDataClone.exclude_business_days && formDataClone.twice_weekly) ? true : false
    },
    code: {
      label: "Code",
      autoComplete: 'code',
      validation: {
        required: false,
      },
      style: {
        flexBasis: '33%',
      },
    },
    // SW2-2152: only shown when the HubSpot Report feature is enabled; optional;
    // editable only by Admin/Billing/Super Admin (TC-01/02).
    ...(featureOn ? {
      cost_value: {
        label: "Cost Value",
        parse: validator.parseDecimal,
        disabled: !canEditCost,
        style: {
          flexBasis: '33%',
        },
      },
    } : {}),
    weight: {
      label: "Weight",
      parse: validator.parseDecimal,
      validation: {
        required: false,
      },
      style: {
        flexBasis: '33%',
      },
    },
    // SW2-2334: no recurrence at all. Overrides every other scheduling field, so it
    // sits first and disables the rest rather than being disabled by them.
    on_demand: {
      label: "On Demand (no recurring schedule)",
      controlType: UIControlType.toggleButton,
      checked: false,
      toggleSize: 'sm' as ToggleSize,
    },
    exclude_business_days: {
      label: "Exclude Non-Business Days",
      controlType: UIControlType.toggleButton,
      checked: false,
      toggleSize: 'sm' as ToggleSize,
      disabled: formDataClone.on_demand || formDataClone.twice_weekly ? true : false
    },
    exclude_weekends: {
      label: "Exclude Weekends",
      controlType: UIControlType.toggleButton,
      checked: false,
      toggleSize: 'sm' as ToggleSize,
      disabled: formDataClone.on_demand || formDataClone.exclude_business_days
    },
    exclude_us_holidays: {
      label: "Exclude US Holidays",
      controlType: UIControlType.toggleButton,
      checked: false,
      toggleSize: 'sm' as ToggleSize,
      disabled: formDataClone.on_demand || formDataClone.exclude_business_days
    },
    ...conditionalFormSchemaProperty,
    twice_weekly: {
      label: "Twice Weekly",
      controlType: UIControlType.toggleButton,
      checked: false,
      toggleSize: 'sm' as ToggleSize,
      disabled: formDataClone.on_demand || formDataClone.exclude_business_days
    },
    ...conditionalTwiceWeeklyProperties
  };
}

const allInspectionOptions = [
  {value: "Sunday"},
  {value: "Monday"}, 
  {value: "Tuesday"},
  {value: "Wednesday"}, 
  {value: "Thursday"}, 
  {value: "Friday"}, 
  {value: "Saturday"}
]

const IntervalModal = (props: any) => {
  const { setshowIntervalModal, isEditAction, selectedIntervalDetails, inspectionIntervalsQuery } = props
  const formContext = useContext(FormContext);
  const appContext = useContext(AppContext);
  const { tenantHasFeature } = useContext(TenantFeatureContext);
  const canEditCost = COST_ROLES.includes(appContext.get('user')?.roleName);
  const featureOn = tenantHasFeature('HubSpot Report');
  const [createResponse, setCreateResponse] = useState({})
  const [editResponse, setEditResponse] = useState({})
  const [deleteResponse, setDeleteResponse] = useState({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [formDataClone, setFormDataClone] = useState(formContext.value)

  const firtInspectionOptions = allInspectionOptions.filter(ele => ele.value !== "Saturday")
  const indexOfFirstInspectionSelection = allInspectionOptions.findIndex(ele => ele.value === formDataClone.week1_first_inspection)
  const secondInspectionOptions = formDataClone.week1_first_inspection ? allInspectionOptions.slice(indexOfFirstInspectionSelection + 1) : allInspectionOptions.map(ele => ele)

  // SW2-2334: On Demand also has to suppress the CONDITIONAL blocks, not just disable the
  // toggles. An interval already carrying twice-weekly settings would otherwise keep
  // rendering its week fields after On Demand is ticked — and they are required, so the
  // row could not be saved at all.
  const conditionalTwiceWeeklyProperties = (!formDataClone.on_demand && !formDataClone.exclude_business_days && formDataClone.twice_weekly) ? 
  {
    week1_first_inspection: {
      label: 'First Inspection',
      controlType: UIControlType.select,
      disabled: (!formDataClone.exclude_business_days && formDataClone.twice_weekly) ? false : true,
      options: firtInspectionOptions,
      labelKey: 'value',
      valueKey: 'value',
      style: {
        flexBasis: '50%',
      },
      validation: {
        required: formDataClone.twice_weekly,
      },
    },
    week1_second_inspection: {
      label: 'Second Inspection',
      controlType: UIControlType.select,
      disabled: (!formDataClone.exclude_business_days && formDataClone.twice_weekly) ? false : true,
      options: secondInspectionOptions,
      labelKey: 'value',
      valueKey: 'value',
      style: {
        flexBasis: '50%',
      },
      validation: {
        required: formDataClone.twice_weekly,
      },
    },
  } : {}

  const handleCreate = async(formData:any) => {
    try{
      const createResponsedata = await inspectionIntervalsApi.post(`/inspection-intervals`, {data: formData})
      
      setEditResponse(createResponsedata)
      setshowIntervalModal(false)
    }
    catch(error){
      console.log(error)
    }
  }

  const handleEdit = async(formData:any) => {
    try{
      const editResponsedata = await inspectionIntervalsApi.update(`/inspection-intervals/${selectedIntervalDetails.id}`, {data: formData})

      await inspectionIntervalsApi.updateNextInspectionDateForIntervalId(selectedIntervalDetails.id)

      setCreateResponse(editResponsedata)
      setshowIntervalModal(false)
    }
    catch(error){
      console.log(error)
    }
  }

  const handleDelete = async() => {
    try{
      const deletedResponsedata = await inspectionIntervalsApi.delete(`/inspection-intervals/${selectedIntervalDetails.id}`)

      setDeleteResponse(deletedResponsedata)
      setshowIntervalModal(false)
    }
    catch(error){
      // Kept open so the reason shows (e.g. 409: forms could not be moved).
      setDeleteError(error?.response?.data?.message || "The interval could not be deleted.")
    }
  }

  const conditionalFormSchemaProperty = (!formDataClone.on_demand && !formDataClone.exclude_business_days && (formDataClone.exclude_weekends || formDataClone.exclude_us_holidays)) ? 
  {
    before_after: {
      label: 'Next inspection to be scheduled',
      controlType: UIControlType.select,
      disabled: (formDataClone.exclude_weekends || formDataClone.exclude_us_holidays) ? false : true,
      options: [{value: "BEFORE"}, {value: "AFTER"}],
      labelKey: 'value',
      valueKey: 'value'
    },
  } : {}

  useEffect(() => {
    inspectionIntervalsQuery.refetch()
  },[createResponse, editResponse, deleteResponse])

  useEffect(() => {
    setFormDataClone(formContext.value)
  },[formContext.value])

  return (
    <>
    <FormModal
      modalProps={{
        title: `${isEditAction ? 'Edit Interval' : 'Create Interval'}`,
        submitBtnText: "Submit",
      }}
      onSubmit={(formData) => isEditAction ? handleEdit(formData) : handleCreate(formData)}
      onCancel={() => setshowIntervalModal(false)}
      showDelete={isEditAction ? true : false}
      onDelete={() => setShowDeleteConfirm(true)}
    >
      <FormSchemaFields
        schema={getSchema(conditionalFormSchemaProperty, conditionalTwiceWeeklyProperties, formDataClone, canEditCost, featureOn)}
        onChange={formContext.set}
        formData={formContext.value}
        initialFormData={isEditAction ? selectedIntervalDetails : {}}
      />
    </FormModal>
    {/* SW2-2306: deleting moves every project on this interval to another one. */}
    <ConfirmationModal
      show={showDeleteConfirm}
      title={`Delete "${selectedIntervalDetails?.name}"?`}
      subTitle={[
        "Projects using this interval will be shifted to the next available interval in their division (the same or a longer frequency), or to the default interval if there is none.",
        "This cannot be undone.",
        ...(deleteError ? [deleteError] : []),
      ]}
      buttonText="Delete Interval"
      handleClose={() => {
        setShowDeleteConfirm(false)
        setDeleteError(null)
      }}
      handleConfirm={handleDelete}
    />
    </>
  );
};

export default IntervalModal;
